import type { MessageKey } from 'types/LocaleMessageKey';

import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import type { Ring } from '@/app/[locale]/product/_hooks/types';
import { getFinishPrice } from '@/app/[locale]/product/_utils/getFinishPrice';
import { RingFinishEnum } from '@/app/enums/RingFinishEnum';
import useShowOldPrice from '@/app/hooks/useShowOldPrice';
import type { RingFinish } from '@/app/types/ring';

import getWizardData from '../utils/getWizardData';
import type { RingsStepData } from '../wizardData/types';

interface UseRingFinishChoice {
  ring: Ring | undefined;
  selectedFinish: RingFinish | undefined;
}

interface UseRingFinishChoiceReturn {
  colorMsgKey: MessageKey;
  currencyCode: string;
  discount: number;
  finish: RingFinish;
  isChosen: boolean;
  localizedAlt: MessageKey;
  originalPrice: number;
  src: string | undefined;
}

interface UseWizardData {
  ring: Ring | undefined;
}

const useRingWizardData = ({ ring }: UseWizardData): RingsStepData | null => {
  const { currentRoute } = useWizardStateContext();
  const style = ring?.style;
  const finish = ring?.finish;
  const accessory = currentRoute.accessory;
  const validAccessory = accessory.isValid;
  const validFinish = currentRoute.finish.isValid;
  const validStyle = currentRoute.style.isValid;
  if (!finish || !style || validAccessory || !validStyle || !validFinish)
    return null;

  return getWizardData({
    accessory: accessory.value,
    style,
    finish,
  });
};

const useRingFinishChoice = ({
  ring,
  selectedFinish,
}: UseRingFinishChoice): UseRingFinishChoiceReturn => {
  const showOldPrice = useShowOldPrice();
  const wizard = useRingWizardData({ ring });
  const finish = ring?.finish ?? RingFinishEnum.Silver;
  const selector = wizard?.finishStep?.selector;
  const isChosen = finish === selectedFinish;

  const localizedAlt = selector?.altMsgKey as MessageKey;
  const src = selector?.originalSrc;
  const colorMsgKey = ring?.colorMsgKey as MessageKey;
  const discountedPrice = ring?.discountedPrice.discountedPrice?.amount;
  const oldPrice = ring?.oldPrice?.amount;
  const price = ring?.price?.amount;
  const currencyCode = ring?.price?.currencyCode ?? 'USD';

  const { originalPrice, discount } = getFinishPrice({
    discountedAmount: discountedPrice ?? 0,
    showOldPrice,
    oldPriceAmount: oldPrice ?? price ?? 0,
    priceAmount: price ?? 0,
  });

  return {
    colorMsgKey,
    finish,
    localizedAlt,
    src,
    originalPrice,
    discount,
    currencyCode,
    isChosen,
  };
};

export default useRingFinishChoice;
